import React from 'react';
import { Box, Typography, Card, CardContent, Stack, Chip, Divider, Paper } from '@mui/material';
import { Schedule, TrendingUp, Warning } from '@mui/icons-material';

// Calculate shift duration in hours
const calculateDuration = (startTs, endTs) => {
  const start = new Date(String(startTs).replace(' ', 'T'));
  const end = new Date(String(endTs).replace(' ', 'T'));
  return Math.round((end - start) / (1000 * 60 * 60) * 10) / 10;
};

// Calculate hours by employee for a specific date range
const calculateEmployeeHours = (employees, shifts, dateRange) => {
  return employees.map(employee => {
    const employeeShifts = shifts.filter(shift => shift.employee_id === employee.id);

    // Calculate daily hours for each day in range
    const dailyHours = dateRange.map(day => {
      const dayShifts = employeeShifts.filter(shift => {
        const shiftStart = new Date(String(shift.start_ts).replace(' ', 'T'));
        return shiftStart.toDateString() === day.toDateString();
      });

      return dayShifts.reduce((sum, shift) =>
        sum + calculateDuration(shift.start_ts, shift.end_ts), 0
      );
    });

    const totalHours = dailyHours.reduce((sum, hours) => sum + hours, 0);
    const avgHours = totalHours / dateRange.length;
    const maxDayHours = Math.max(...dailyHours);

    return {
      employee,
      dailyHours,
      totalHours,
      avgHours,
      maxDayHours,
      isOvertime: maxDayHours > 8 || totalHours > 40
    };
  }).filter(emp => emp.totalHours > 0);
};

export default function HoursSummary({ employees, shifts, dateRange, viewMode, compact = true }) {
  const employeeHours = calculateEmployeeHours(employees, shifts, dateRange);
  const totalScheduledHours = employeeHours.reduce((sum, emp) => sum + emp.totalHours, 0);
  const avgHoursPerEmployee = employeeHours.length > 0 ? totalScheduledHours / employeeHours.length : 0;
  const overtimeEmployees = employeeHours.filter(emp => emp.isOvertime);

  if (employeeHours.length === 0) {
    return null;
  }

  if (compact) {
    // Flat, single-row summary bar
    return (
      <Paper elevation={0} sx={{ m: 1, mt: 0, px: 1.5, py: 1, borderTop: '1px solid #e5e7eb', backgroundColor: 'background.paper' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ overflowX: 'auto' }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Schedule sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {totalScheduledHours.toFixed(1)}h
            </Typography>
            <Chip size="small" label={`${dateRange.length}d`} sx={{ height: 20, fontSize: 11 }} />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Avg/Emp: {avgHoursPerEmployee.toFixed(1)}h
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Employees: {employeeHours.length}
          </Typography>

          {overtimeEmployees.length > 0 && (
            <Chip
              size="small"
              color="warning"
              label={`Overtime: ${overtimeEmployees.length}`}
              sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
            />
          )}
        </Stack>
      </Paper>
    );
  }

  // Legacy detailed card (unused by default)
  return (
    <Card sx={{ m: 2, mt: 0, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Schedule sx={{ color: 'primary.main' }} /> Hours Summary
          <Chip label={`${dateRange.length} day${dateRange.length !== 1 ? 's' : ''}`} size="small" sx={{ ml: 1 }} />
        </Typography>
        <Stack direction="row" spacing={3}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main', lineHeight: 1 }}>{totalScheduledHours.toFixed(1)}</Typography>
            <Typography variant="caption" color="text.secondary">Total Hours</Typography>
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1 }}>{avgHoursPerEmployee.toFixed(1)}</Typography>
            <Typography variant="caption" color="text.secondary">Avg per Employee</Typography>
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>{employeeHours.length}</Typography>
            <Typography variant="caption" color="text.secondary">Scheduled Employees</Typography>
          </Box>
          {overtimeEmployees.length > 0 && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main', lineHeight: 1 }}>{overtimeEmployees.length}</Typography>
              <Typography variant="caption" color="text.secondary">Overtime Risk</Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
